import React, { useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useUIContext } from "../../../../../context/UIContext";
import { filterSessionsForSettingsScope } from "../../../../../util/billing/normalizeBillingEntities";

function tempItemId() {
  return `temp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

/** 이전 기간 항목 → 현재 세션 로컬 상태 병합 (item_name 기준 없는 것만) */
function mergePreviousItemsIntoSession(
  prevItems,
  selectedSessionId,
  rawSource,
  normalizeItemName,
  newTempId,
) {
  const targetRows = prevItems.filter(
    (si) => si.billing_session_id === selectedSessionId,
  );
  const others = prevItems.filter(
    (si) => si.billing_session_id !== selectedSessionId,
  );
  const existingNames = new Set(
    targetRows.map((si) => normalizeItemName(si.item_name)).filter(Boolean),
  );
  const toAdd = [];
  let maxOrder = targetRows.reduce(
    (m, si) => Math.max(m, Number(si.display_order) || 0),
    0,
  );
  for (const src of rawSource) {
    const name = normalizeItemName(src.item_name);
    if (!name || existingNames.has(name)) continue;
    existingNames.add(name);
    maxOrder += 1;
    let def =
      src.default_amount === null || src.default_amount === undefined
        ? null
        : Number(src.default_amount);
    if (def !== null && Number.isNaN(def)) def = null;
    toAdd.push({
      id: newTempId(),
      billing_session_id: selectedSessionId,
      item_name: name,
      default_amount: def,
      display_order: maxOrder,
    });
  }
  if (toAdd.length === 0) return { next: null, added: 0 };
  return {
    next: [...others, ...targetRows, ...toAdd],
    added: toAdd.length,
  };
}

export default function BillingItemMasterManager({
  sessions,
  sessionItems,
  setSessionItems,
  selectedSessionId,
  settingData,
  handleToastCenterTop,
  billingApi,
  canCreate,
  canUpdate,
  canDelete,
}) {
  const { t } = useTranslation();
  const canMutateItems = Boolean(canCreate || canUpdate);
  const { setToastConfirmModalState } = useUIContext();

  const [prevSchoolYearId, setPrevSchoolYearId] = useState("");
  const [prevCourse, setPrevCourse] = useState("");
  const [prevSessionId, setPrevSessionId] = useState("");
  const [confirmDelete, setConfirmDelete] = useState([]);
  const [loadPreviousLoading, setLoadPreviousLoading] = useState(false);
  const dragIdx = useRef(null);

  const { syncSessionItemsMutation, deleteSessionItemMutation, fetchSessionItems } =
    billingApi;

  const currentItems = useMemo(
    () =>
      sessionItems
        .filter((si) => si.billing_session_id === selectedSessionId)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0)),
    [sessionItems, selectedSessionId],
  );

  const selectedSession = sessions.find((s) => s.id === selectedSessionId);

  const schoolYearIdsOnSessions = useMemo(() => {
    const set = new Set(
      (sessions || []).map((s) => String(s.school_year_id)).filter(Boolean),
    );
    return [...set].sort((a, b) => Number(b) - Number(a));
  }, [sessions]);

  const schoolYearLabel = (yearIdStr) => {
    const row = settingData?.find(
      (s) => s.hierarchy_level === 0 && String(s.id) === String(yearIdStr),
    );
    return row ? `${row.school_year}` : yearIdStr;
  };

  const prevCourseOptions = useMemo(() => {
    if (!settingData || !prevSchoolYearId) return [];
    const yearEntry = settingData.find(
      (s) =>
        s.hierarchy_level === 0 && String(s.id) === String(prevSchoolYearId),
    );
    if (!yearEntry) return [];
    return settingData.filter(
      (s) => s.hierarchy_level === 1 && s.parent_id === yearEntry.id,
    );
  }, [settingData, prevSchoolYearId]);

  const prevSessionOptions = useMemo(() => {
    if (!prevSchoolYearId || !prevCourse) return [];
    return filterSessionsForSettingsScope(
      sessions,
      prevSchoolYearId,
      prevCourse,
    ).filter((s) => s.id !== selectedSessionId);
  }, [sessions, prevSchoolYearId, prevCourse, selectedSessionId]);

  const handleFieldChange = (id, field, value) => {
    setSessionItems((prev) =>
      prev.map((si) => {
        if (si.id !== id) return si;
        if (field === "default_amount") {
          return {
            ...si,
            default_amount:
              value === "" || value === null ? null : Number(value),
          };
        }
        return { ...si, [field]: value };
      }),
    );
  };

  const handleAdd = () => {
    if (!canCreate) {
      handleToastCenterTop(
        t("billingManagement.permissions.noAdd") || "추가 권한이 없습니다.",
      );
      return;
    }
    setSessionItems((prev) => [
      ...prev,
      {
        id: tempItemId(),
        billing_session_id: selectedSessionId,
        item_name: "",
        default_amount: null,
        display_order: currentItems.length + 1,
      },
    ]);
  };

  const handleConfirmDelete = (id) => setConfirmDelete((p) => [...p, id]);
  const handleCancelDelete = () => setConfirmDelete([]);

  const handleDelete = (id) => {
    if (!canDelete) {
      handleToastCenterTop(
        t("billingManagement.permissions.noDelete") || "삭제 권한이 없습니다.",
      );
      return;
    }
    if (String(id).startsWith("temp_")) {
      setSessionItems((prev) => prev.filter((si) => si.id !== id));
      setConfirmDelete([]);
      return;
    }
    setToastConfirmModalState({
      visible: true,
      message:
        t("billingManagement.messages.itemDeleteConfirm") ||
        "해당 항목을 삭제하면 이 항목에 속한 학생들 기록도 모두 삭제 됩니다. 정말 삭제하시겠습니까?",
      onConfirm: () => {
        deleteSessionItemMutation.mutate(id, {
          onSuccess: () => {
            setConfirmDelete([]);
            handleToastCenterTop(
              t("billingManagement.messages.itemDeleted") ||
                "항목이 삭제되었습니다.",
            );
          },
          onError: (e) =>
            handleToastCenterTop(
              e?.response?.data?.message ||
                e?.message ||
                t("billingManagement.errors.deleteFailed") || "삭제 실패",
            ),
        });
      },
      onCancel: () => {},
    });
  };

  const normalizeItemName = (name) => String(name ?? "").trim();

  /** 같은 기간(세션) 안에서 항목명 중복 불가 */
  const validateItemNamesForSave = () => {
    const trimmed = currentItems.map((si) => normalizeItemName(si.item_name));
    if (trimmed.some((n) => n === "")) {
      handleToastCenterTop(
        t("billingManagement.messages.itemNameRequired") ||
          "항목명을 입력해주세요.",
      );
      return false;
    }
    const seen = new Set();
    for (const n of trimmed) {
      if (seen.has(n)) {
        handleToastCenterTop(
          t("billingManagement.messages.itemDuplicateName") ||
            "이 기간에서 동일한 항목명은 중복될 수 없습니다.",
        );
        return false;
      }
      seen.add(n);
    }
    return true;
  };

  const handleSaveAll = () => {
    if (!canMutateItems) {
      handleToastCenterTop(
        t("billingManagement.permissions.noSave") || "저장 권한이 없습니다.",
      );
      return;
    }
    if (!validateItemNamesForSave()) return;

    const itemsPayload = currentItems.map((si, i) => {
      const row = {
        item_name: normalizeItemName(si.item_name),
        default_amount:
          si.default_amount === "" || si.default_amount === undefined
            ? null
            : Number(si.default_amount),
        display_order: i + 1,
      };
      if (si.id && !String(si.id).startsWith("temp_")) {
        row.id = si.id;
      }
      return row;
    });

    syncSessionItemsMutation.mutate(
      { sessionId: selectedSessionId, items: itemsPayload },
      {
        onSuccess: () =>
          handleToastCenterTop(
            t("billingManagement.messages.saved") || "저장되었습니다.",
          ),
        onError: (e) =>
          handleToastCenterTop(
            e?.response?.data?.message ||
              e?.message ||
              t("billingManagement.errors.saveFailed") || "저장 실패",
          ),
      },
    );
  };

  const handleLoadPreviousItems = async () => {
    if (!canMutateItems) {
      handleToastCenterTop(
        t("billingManagement.permissions.noLoad") || "불러오기 권한이 없습니다.",
      );
      return;
    }
    if (!prevSessionId) {
      handleToastCenterTop(
        t("billingManagement.messages.selectPastPeriod") ||
          "지난 기간을 선택해주세요.",
      );
      return;
    }
    if (!selectedSessionId) return;

    setLoadPreviousLoading(true);
    try {
      let rawSource = sessionItems
        .filter((si) => si.billing_session_id === prevSessionId)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

      if (rawSource.length === 0 && fetchSessionItems) {
        rawSource = await fetchSessionItems(prevSessionId);
      }

      if (!rawSource.length) {
        handleToastCenterTop(
          t("billingManagement.messages.itemLoadNoItemsInPeriod") ||
            "선택한 기간에 불러올 항목이 없습니다.",
        );
        return;
      }

      const outcome = mergePreviousItemsIntoSession(
        sessionItems,
        selectedSessionId,
        rawSource,
        normalizeItemName,
        tempItemId,
      );

      if (!outcome.next) {
        handleToastCenterTop(
          t("billingManagement.messages.itemNoNewFromPast") ||
            "이미 동일한 항목명이 있어 추가된 항목이 없습니다.",
        );
        return;
      }
      setSessionItems(outcome.next);
      handleToastCenterTop(
        t("billingManagement.messages.itemsLoadedApplyHint", {
          count: outcome.added,
        }) ||
          `항목 ${outcome.added}개를 화면에 반영했습니다. 저장을 누르면 서버에 적용됩니다.`,
      );
    } catch (e) {
      handleToastCenterTop(
        e?.response?.data?.message ||
          e?.message ||
          t("billingManagement.errors.loadFailed") || "불러오기 실패",
      );
    } finally {
      setLoadPreviousLoading(false);
    }
  };

  const handleDragStart = (idx) => {
    if (!canUpdate) return;
    dragIdx.current = idx;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (dropIndex) => {
    if (!canUpdate) return;
    const from = dragIdx.current;
    if (from === null || from === dropIndex) return;

    const reordered = [...currentItems];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(dropIndex, 0, moved);

    const orders = reordered.map((si, i) => ({
      id: si.id,
      display_order: i + 1,
    }));

    setSessionItems((prev) =>
      prev.map((si) => {
        if (si.billing_session_id !== selectedSessionId) return si;
        const o = orders.find((x) => x.id === si.id);
        return o ? { ...si, display_order: o.display_order } : si;
      }),
    );

    dragIdx.current = null;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">
          {t("billingManagement.labels.itemManagement") || "항목 관리"}
          {selectedSession && (
            <small className="text-muted ms-2">
              (
              {selectedSession.session_name ||
                t("billingManagement.labels.unnamed") ||
                "이름 없음"}
              )
            </small>
          )}
        </h6>
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={handleAdd}
          disabled={!selectedSessionId || !canCreate}
        >
          {t("common.labels.add") || "추가"}
        </button>
      </div>

      <div
        className="mb-3 p-2 border rounded bg-light"
        style={{ fontSize: "13px" }}
      >
        <div className="text-muted small fw-bold mb-1">
          {t("billingManagement.labels.loadFromPastSection") ||
            "이전 데이터 불러오기:"}
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <select
            className="form-select form-select-sm"
            style={{ width: 100 }}
            value={prevSchoolYearId}
            disabled={!canMutateItems}
            onChange={(e) => {
              setPrevSchoolYearId(e.target.value);
              setPrevCourse("");
              setPrevSessionId("");
            }}
          >
            <option value="">
              {t("billingManagement.labels.schoolYearOption") || "학년도"}
            </option>
            {schoolYearIdsOnSessions.map((yid) => (
              <option key={yid} value={yid}>
                {schoolYearLabel(yid)}
              </option>
            ))}
          </select>
          <select
            className="form-select form-select-sm"
            style={{ width: 100 }}
            value={prevCourse}
            onChange={(e) => {
              setPrevCourse(e.target.value);
              setPrevSessionId("");
            }}
            disabled={!canMutateItems || !prevSchoolYearId}
          >
            <option value="">
              {t("billingManagement.labels.courseOption") || "과정"}
            </option>
            <option value="__INTEGRATED__">
              {t("billingManagement.labels.integrated") || "통합"}
            </option>
            {prevCourseOptions.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.group_name}
              </option>
            ))}
          </select>
          <select
            className="form-select form-select-sm"
            style={{ width: 100 }}
            value={prevSessionId}
            onChange={(e) => setPrevSessionId(e.target.value)}
            disabled={!canMutateItems || !prevCourse}
          >
            <option value="">
              {t("billingManagement.labels.periodOption") || "기간"}
            </option>
            {prevSessionOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.session_name}
              </option>
            ))}
          </select>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={handleLoadPreviousItems}
            disabled={
              !canMutateItems || !prevSessionId || loadPreviousLoading
            }
          >
            {t("billingManagement.labels.load") || "불러오기"}
          </button>
        </div>
      </div>

      <table
        className="table table-bordered table-sm mb-0"
        style={{ fontSize: "13px" }}
      >
        <thead className="table-light">
          <tr>
            <th
              style={{ width: 40 }}
              title={
                t("billingManagement.labels.dragOrderTitle") ||
                "번호 칸을 드래그하면 순서 변경"
              }
            >
              {t("common.labels.order") || "순서"}
            </th>
            <th>{t("billingManagement.labels.itemName") || "항목명"}</th>
            <th style={{ width: 100 }}>
              {t("billingManagement.labels.defaultAmount") || "기본금액"}
            </th>
            <th style={{ width: 51 }}>
              <button
                className="btn btn-sm btn-primary m-0 px-2 w-100"
                style={{ fontSize: "11px" }}
                onClick={handleSaveAll}
                disabled={
                  !canMutateItems || syncSessionItemsMutation.isLoading
                }
              >
                {t("billingManagement.labels.save") || "저장"}
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {currentItems.length === 0 ? (
            <tr>
              <td colSpan={4} className="text-center text-muted py-3">
                {t("billingManagement.labels.noItems") || "항목이 없습니다."}
              </td>
            </tr>
          ) : (
            currentItems.map((si, idx) => (
              <tr
                key={si.id}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(idx)}
              >
                <td
                  className="text-center align-middle text-muted"
                  draggable={canUpdate}
                  title={
                    t("billingManagement.labels.dragHandleTitle") ||
                    "이 칸을 드래그하여 순서 변경"
                  }
                  onDragStart={() => handleDragStart(idx)}
                  style={{
                    cursor: canUpdate ? "grab" : "default",
                    userSelect: "none",
                  }}
                >
                  {idx + 1}
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={si.item_name}
                    disabled={!canMutateItems}
                    onChange={(e) =>
                      handleFieldChange(si.id, "item_name", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control form-control-sm text-end"
                    value={si.default_amount ?? ""}
                    disabled={!canMutateItems}
                    onChange={(e) =>
                      handleFieldChange(si.id, "default_amount", e.target.value)
                    }
                  />
                </td>
                <td className="text-center align-middle">
                  {confirmDelete.includes(si.id) ? (
                    <div className="d-flex flex-column gap-1">
                      <button
                        className="btn btn-sm btn-danger m-0 px-1"
                        style={{ fontSize: "11px" }}
                        onClick={() => handleDelete(si.id)}
                        disabled={!canDelete}
                      >
                        {t("common.labels.confirm") || "확인"}
                      </button>
                      <button
                        className="btn btn-sm btn-secondary m-0 px-1"
                        style={{ fontSize: "11px" }}
                        onClick={() => handleCancelDelete()}
                      >
                        {t("common.labels.cancel") || "취소"}
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn btn-sm btn-warning m-0 px-2"
                      style={{ fontSize: "11px" }}
                      onClick={() => handleConfirmDelete(si.id)}
                      disabled={!canDelete}
                    >
                      {t("common.labels.delete") || "삭제"}
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
